import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Zone } from '@popcart/core/v1';
import { api, errorMessage } from '../../api';
import { ConfirmDialog, EmptyState, ErrorState, Loading, useTitle } from '../../ui/bits';
import { Fig, PageHead, Sheet } from '../../ui/viz';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/toast';
import { count, peso } from '../../lib/format.js';
import { describeField, isDirty, specFor, toDraft, validateSettings } from '../../lib/settings.js';
import { opsKeys, useOpsSettings } from '../queries';
import { FieldError, StaleNote, TextField } from '../payments/kit';
import { RuleField, type RuleKind } from '../catalogue/RuleField';
import { putRules } from '../catalogue/lib.js';
import { ZoneMap } from './ZoneMap';
import { FeeLadder } from './FeeLadder';
import { ItemMenu } from './Menu';
import { PUROK_MAX, ZONE_NAME_MAX, addPoint, areaKm2, areaLabel, enforcementNote, openRing, saveZone, sortZones, undoPoint, zoneKeys, type LngLat } from './lib.js';
import './zones.css';

type Editor = { zoneId: string | null; name: string; purok: string; points: LngLat[] };
type Server = Record<string, Record<string, unknown>>;

/**
 * /ops/zones: delivery zones drawn on the PopCart map (click points, Undo point, Clear, Save),
 * the zone list with active switches and how many saved addresses fall inside, and the delivery
 * fee settings with the enforceZones switch.
 */
export function OpsZonesPage() {
  useTitle('Zones & fees');
  const qc = useQueryClient();
  const toast = useToast();
  const zonesQ = useQuery({ queryKey: zoneKeys.all, queryFn: () => api.opsZones() });
  const zones = zonesQ.data ?? [];
  const [editor, setEditor] = useState<Editor | null>(null);
  const [errors, setErrors] = useState<{ name?: string; purok?: string; points?: string }>({});
  const [deleting, setDeleting] = useState<Zone | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const replace = (z: Zone) => qc.setQueryData<Zone[]>(zoneKeys.all, (old) => (old ? (old.some((x) => x.id === z.id) ? old.map((x) => (x.id === z.id ? z : x)) : [...old, z]) : [z]));

  const save = useMutation({
    mutationFn: (e: Editor) => saveZone(api, e.zoneId, e),
    onSuccess: (r, e) => {
      if (!r.ok) {
        setErrors(r.errors);
        return;
      }
      replace(r.zone);
      toast(e.zoneId ? `${r.zone.name} is saved.` : `${r.zone.name} is drawn and active.`);
      setEditor(null);
      setErrors({});
      setSelected(r.zone.id);
      void qc.invalidateQueries({ queryKey: zoneKeys.all });
    },
    onError: (err) => toast(errorMessage(err, "Couldn't save the zone. Try again."), 'error'),
  });

  const toggle = useMutation({
    mutationFn: (z: Zone) => api.updateZone(z.id, { isActive: !z.isActive }),
    onSuccess: (z) => {
      replace(z);
      toast(z.isActive ? `${z.name} is on.` : `${z.name} is off. It stays on the map.`);
    },
    onError: (err) => toast(errorMessage(err, "Couldn't change the zone. Try again."), 'error'),
    onSettled: () => void qc.invalidateQueries({ queryKey: zoneKeys.all }),
  });

  const remove = useMutation({
    mutationFn: (z: Zone) => api.deleteZone(z.id),
    onSuccess: (_r, z) => {
      qc.setQueryData<Zone[]>(zoneKeys.all, (old) => old?.filter((x) => x.id !== z.id));
      toast(`${z.name} is deleted.`);
      setDeleting(null);
      if (editor?.zoneId === z.id) setEditor(null);
    },
    onError: (err) => toast(errorMessage(err, "Couldn't delete the zone. Try again."), 'error'),
    onSettled: () => void qc.invalidateQueries({ queryKey: zoneKeys.all }),
  });

  const startNew = () => {
    setErrors({});
    setSelected(null);
    setEditor({ zoneId: null, name: '', purok: '', points: [] });
  };
  const startEdit = (z: Zone) => {
    setErrors({});
    setSelected(z.id);
    setEditor({ zoneId: z.id, name: z.name, purok: z.purok ?? '', points: openRing(z.polygon) });
  };
  const setPoints = (fn: (pts: LngLat[]) => LngLat[]) => {
    setEditor((e) => (e ? { ...e, points: fn(e.points) } : e));
    setErrors((x) => ({ ...x, points: undefined }));
  };

  const active = zones.filter((z) => z.isActive);
  const covered = active.reduce((n, z) => n + (z.addressesInside ?? 0), 0);

  return (
    <>
      <PageHead
        title="Zones & fees"
        meta={zonesQ.data ? `${count(active.length)} active · ${count(zones.length)} ${zones.length === 1 ? 'zone' : 'zones'}${active.length ? ` · ${count(covered)} ${covered === 1 ? 'address' : 'addresses'} covered` : ''}` : undefined}
        actions={
          editor ? null : (
            <button type="button" className="btn btn-secondary" onClick={startNew}>
              <Icon name="plus" />
              Draw a zone
            </button>
          )
        }
      />

      <div className="dv-grid">
        <section className="card dv-card dv-span-8 dv-rise zn-mapcard" aria-labelledby="zn-map-h" style={{ ['--i' as string]: 0 }}>
          <div className="dv-card-h">
            <h2 id="zn-map-h">{editor ? (editor.zoneId ? `Editing ${editor.name || 'zone'}` : 'New zone') : 'Map'}</h2>
            {editor ? (
              <span className="dv-chip" aria-live="polite">
                {count(editor.points.length)} {editor.points.length === 1 ? 'point' : 'points'} · {areaLabel(areaKm2(editor.points))}
              </span>
            ) : null}
            <span className="grow" />
            {editor ? (
              <>
                <span className="dv-sub zn-hint">Click the map, in order</span>
                <button type="button" className="dv-ibtn press" disabled={!editor.points.length} onClick={() => setPoints(undoPoint)} aria-label="Undo point" title="Undo point">
                  <Icon name="refund" />
                </button>
                <button type="button" className="dv-ibtn press" disabled={!editor.points.length} onClick={() => setPoints(() => [])} aria-label="Clear" title="Clear all points">
                  <Icon name="close" />
                </button>
              </>
            ) : null}
          </div>
          <ZoneMap
            zones={zones}
            editingId={editor?.zoneId ?? null}
            points={editor?.points ?? []}
            drawing={editor !== null}
            onAddPoint={(p) => setPoints((pts) => addPoint(pts, p))}
            onSelect={setSelected}
          />
          <FieldError id="zn-points-err">{errors.points}</FieldError>
        </section>

        <section className="card dv-card dv-span-4 dv-rise zn-side" aria-labelledby="zn-side-h" style={{ ['--i' as string]: 1 }}>
          {editor ? (
            <ZoneForm
              editor={editor}
              errors={errors}
              busy={save.isPending}
              onChange={(patch) => {
                setEditor((e) => (e ? { ...e, ...patch } : e));
                setErrors((x) => ({ ...x, ...Object.fromEntries(Object.keys(patch).map((k) => [k, undefined])) }));
              }}
              onSave={() => save.mutate(editor)}
              onCancel={() => {
                setEditor(null);
                setErrors({});
              }}
            />
          ) : null}
          <div className="dv-card-h">
            <h2 id="zn-side-h">Zones</h2>
            {zonesQ.data ? <span className="dv-chip">{count(zones.length)}</span> : null}
          </div>
          {zonesQ.isError && !zonesQ.data ? (
            <ErrorState error={zonesQ.error} onRetry={() => void zonesQ.refetch()} />
          ) : !zonesQ.data ? (
            <Loading label="Loading zones" rows={2} />
          ) : zones.length === 0 ? (
            <EmptyState icon="globe" title="No zones yet">
              Draw where you deliver. Until zone checks are on, distance decides.
            </EmptyState>
          ) : (
            <>
              <StaleNote show={zonesQ.isError} what="zones" />
              <ul className="zn-list">
                {sortZones(zones).map((z, j) => (
                  <li key={z.id} className={`zn-zone${z.id === selected ? ' sel' : ''}${z.isActive ? '' : ' off'}`} aria-current={z.id === selected || undefined} style={{ ['--i' as string]: j }}>
                    <div className="zn-zone-top">
                      <span className="zn-dot" aria-hidden="true" />
                      <span className="zn-name">
                        <b>{z.name}</b>
                        {z.purok ? <span className="dv-sub">{z.purok}</span> : null}
                      </span>
                      <button
                        type="button"
                        role="switch"
                        className="switch"
                        aria-checked={z.isActive}
                        disabled={toggle.isPending && toggle.variables?.id === z.id}
                        onClick={() => toggle.mutate(z)}
                        aria-label={`${z.name} active`}
                        title={z.isActive ? 'On' : 'Off'}
                      >
                        <span className="track" aria-hidden="true" />
                      </button>
                    </div>
                    <div className="zn-zone-foot">
                      <span className="zn-fig">
                        <Fig text={areaLabel(z.areaKm2)} size="sm" />
                        <span className="dv-sub">area</span>
                      </span>
                      <span className="zn-fig">
                        <Fig text={count(z.addressesInside)} size="sm" />
                        <span className="dv-sub">{z.addressesInside === 1 ? 'address' : 'addresses'}</span>
                      </span>
                      <span className="grow" />
                      <button type="button" className="btn btn-quiet zn-edit" disabled={editor !== null} onClick={() => startEdit(z)} aria-label={`Edit ${z.name}`}>
                        Edit
                      </button>
                      <ItemMenu
                        label={`More for ${z.name}`}
                        items={[{ label: 'Delete', ariaLabel: `Delete ${z.name}`, icon: 'close', tone: 'danger', disabled: editor !== null, onSelect: () => setDeleting(z) }]}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        <DeliverySettings zones={zones} secondary={editor !== null} />
      </div>

      <ConfirmDialog
        open={deleting !== null}
        title={`Delete ${deleting?.name ?? 'this zone'}?`}
        body={deleting && deleting.isActive ? `${deleting.name} is active. With zone checks on, addresses only inside it can't order after this.` : 'It leaves the map. Turning it off instead keeps the shape.'}
        confirmLabel="Delete zone"
        busy={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting)}
        onClose={() => setDeleting(null)}
      />
    </>
  );
}

function ZoneForm({ editor, errors, busy, onChange, onSave, onCancel }: { editor: Editor; errors: { name?: string; purok?: string }; busy: boolean; onChange(p: Partial<Editor>): void; onSave(): void; onCancel(): void }) {
  const nameRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  useEffect(() => {
    if (errors.name) nameRef.current?.focus();
  }, [errors.name]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSave();
  };
  return (
    <form className="stack s12 zn-form" onSubmit={submit} noValidate aria-label={editor.zoneId ? 'Edit zone' : 'New zone'}>
      <TextField id="zn-name" label="Zone name" value={editor.name} onChange={(v) => onChange({ name: v })} error={errors.name} maxLength={ZONE_NAME_MAX} placeholder="Brgy. Pinyahan" inputRef={nameRef} />
      <TextField id="zn-purok" label="Purok or area (optional)" value={editor.purok} onChange={(v) => onChange({ purok: v })} error={errors.purok} maxLength={PUROK_MAX} placeholder="Purok 3" />
      <div className="row ops-dialog-actions">
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>
          <Icon name="check" />
          {busy ? 'Saving…' : 'Save zone'}
        </button>
      </div>
    </form>
  );
}

/** Short names for the fee figures (the settings spec's labels are sentences). */
const FEE_FIGS: { name: string; label: string }[] = [
  { name: 'baseFeeCentavos', label: 'Starts at' },
  { name: 'includedKm', label: 'Included' },
  { name: 'perKmCentavos', label: 'Each extra km' },
  { name: 'maxKm', label: 'Up to' },
  { name: 'extraStopCentavos', label: 'Extra pickup' },
  { name: 'clusterRadiusKm', label: 'Shared rider' },
];

function figText(kind: string, v: unknown): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return '—';
  if (kind === 'peso') return peso(v);
  if (kind === 'km') return `${v} km`;
  return String(v);
}

/** The fee figures in a row: label over figure; the full sentence rides along as a tooltip. */
function FeeFigs({ value }: { value: Record<string, unknown> }) {
  const spec = specFor('delivery');
  return (
    <div className="st-figs">
      {FEE_FIGS.map((x) => {
        const f = spec.fields.find((ff) => ff.name === x.name);
        return (
          <div key={x.name} title={f ? describeField(f, value[x.name]) : undefined}>
            <span className="dv-label">{x.label}</span>
            <Fig text={figText(f?.kind ?? '', value[x.name])} size="md" />
          </div>
        );
      })}
    </div>
  );
}

function DeliverySettings({ zones, secondary }: { zones: Zone[]; secondary: boolean }) {
  const settings = useOpsSettings();
  const qc = useQueryClient();
  const toast = useToast();
  const server = settings.data as unknown as Server | undefined;
  const delivery = server?.delivery;
  const [draft, setDraft] = useState<Record<string, string | boolean> | null>(null);
  const [enforce, setEnforce] = useState<boolean | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (delivery && draft === null) {
      setDraft(toDraft('delivery', delivery));
      setEnforce(delivery.enforceZones === true);
    }
  }, [delivery, draft]);

  const save = useMutation({
    mutationFn: (value: Record<string, unknown>) => putRules(api, 'delivery', value),
    onSuccess: (fresh) => {
      qc.setQueryData(opsKeys.settings, (old: unknown) => (old ? { ...(old as Server), delivery: fresh } : old));
      setDraft(toDraft('delivery', fresh));
      setEnforce(fresh.enforceZones === true);
      setOpen(false);
      toast('Delivery settings saved. New orders use them right away.');
    },
    onError: (err) => {
      toast(errorMessage(err, "Couldn't save the delivery settings. Try again."), 'error');
      void qc.invalidateQueries({ queryKey: opsKeys.settings });
    },
  });

  const spec = specFor('delivery');
  if (settings.isError && !delivery)
    return (
      <section className="card dv-card dv-span-12">
        <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
      </section>
    );
  if (!delivery || !draft || enforce === null)
    return (
      <div className="dv-span-12">
        <Loading label="Loading delivery settings" rows={2} />
      </div>
    );

  const dirty = isDirty('delivery', draft, delivery) || enforce !== (delivery.enforceZones === true);
  const check = validateSettings('delivery', draft, delivery);
  const preview: Record<string, unknown> = check.ok ? check.value : delivery;
  const savedEnforce = delivery.enforceZones === true;

  const reset = () => {
    setDraft(toDraft('delivery', delivery));
    setEnforce(savedEnforce);
    setErrors({});
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const r = validateSettings('delivery', draft, delivery);
    if (!r.ok) {
      setErrors(r.errors);
      toast('Some values need fixing before saving.', 'error');
      return;
    }
    setErrors({});
    save.mutate({ ...pickDelivery(r.value, spec.fields.map((f) => f.name)), enforceZones: enforce });
  };

  return (
    <section className="card dv-card dv-span-12 dv-rise" aria-labelledby="zn-fees-h" style={{ ['--i' as string]: 2 }}>
      <div className="dv-card-h">
        <h2 id="zn-fees-h">Delivery fee</h2>
        <span className={savedEnforce ? 'dv-chip good' : 'dv-chip'} title={enforcementNote(savedEnforce, zones)}>
          Zone checks {savedEnforce ? 'on' : 'off'}
        </span>
        {dirty && !open ? <span className="dv-chip bad">Unsaved</span> : null}
        <span className="grow" />
        <button type="button" className="btn btn-secondary" onClick={() => setOpen(true)}>
          Edit fees
        </button>
      </div>
      <div className="zn-fees">
        <FeeFigs value={delivery} />
        <div className="zn-ladder">
          <div className="zn-ladder-h">
            <span className="dv-label">Fee by distance</span>
            <span className="st-key">
              <i className="bs" aria-hidden="true" />
              Base
              <i className="ex" aria-hidden="true" />
              Per km
            </span>
          </div>
          <FeeLadder delivery={delivery} />
        </div>
      </div>

      <Sheet open={open} title="Delivery fees" onClose={() => setOpen(false)}>
        <form className="stack s16" aria-label="Delivery fees" onSubmit={submit} noValidate>
          <div className="st-panel">
            <RuleField id="zn-enforce" kind="bool" label="Only deliver inside active zones" value={enforce} onChange={(v) => setEnforce(v === true)} />
            <p className="dv-sub" aria-live="polite">
              {enforcementNote(enforce, zones)}
            </p>
          </div>
          <div className="st-fields">
            {spec.fields.map((f) => (
              <RuleField
                key={f.name}
                id={`zn-${f.name}`}
                kind={f.kind as RuleKind}
                label={f.label}
                help={f.help}
                value={draft[f.name] ?? ''}
                error={errors[f.name]}
                onChange={(v) => {
                  setDraft((d) => (d ? { ...d, [f.name]: v } : d));
                  setErrors((x) => ({ ...x, [f.name]: '' }));
                }}
              />
            ))}
          </div>
          <div className="st-panel">
            <span className="dv-label">Preview</span>
            <FeeLadder delivery={preview} compact />
            <ul className="sr-only" aria-live="polite">
              {spec.fields.map((f) => (
                <li key={f.name}>{describeField(f, preview[f.name])}</li>
              ))}
            </ul>
          </div>
          <div className="st-savebar">
            <span className={dirty ? 'dv-chip bad' : 'dv-chip good'} aria-live="polite">
              {dirty ? 'Unsaved changes' : 'All changes saved'}
            </span>
            <span className="grow" />
            <button type="button" className="btn btn-quiet" disabled={!dirty || save.isPending} onClick={reset}>
              Discard
            </button>
            <button type="submit" className={secondary ? 'btn btn-secondary' : 'btn btn-primary'} disabled={!dirty || save.isPending} aria-busy={save.isPending || undefined}>
              {save.isPending ? 'Saving…' : 'Save delivery settings'}
            </button>
          </div>
        </form>
      </Sheet>
    </section>
  );
}

/** Only the fields this form edits go in the PUT (the API merges the rest). */
function pickDelivery(value: Record<string, unknown>, names: string[]): Record<string, unknown> {
  return Object.fromEntries(names.filter((n) => n in value).map((n) => [n, value[n]]));
}
